import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import {
  AppButton,
  CardProfile,
  SettingItem,
  StickyHeaderNavigation,
} from '@/components'
import { useLogout, useGetFeaturesSettings } from '@/domain'
import { useUserAuthSessionStore } from '@/store'
import { useUserAuthSession } from '@/hooks'
import { settingsActions } from '@/constants/settingsActions'
import { Plus, ReactIcon, Support } from '@/assets/icons'
import config from '@/config/env'

export const ProfileSettingsScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const enableAiBanner = config.VITE_OPENAI_KEY

  // Determine where we navigated from
  const handleBackPress = () => {
    const locationState = location.state as { from?: string } | null
    if (locationState?.from === '/send-request') {
      navigate('/send-request')
    } else {
      navigate('/dashboard')
    }
  }

  const { mutateAsync: logout, isPending } = useLogout()
  const { token } = useUserAuthSessionStore()
  const { endSession } = useUserAuthSession({
    afterEndSession: () => {
      setTimeout(() => {
        window.location.href = '/' // Full page reload like in original
      }, 300)
    },
  })

  const handleLogout = async () => {
    if (token) {
      try {
        await logout()
      } catch (error) {
        // Ignore logout errors
      }
    }

    endSession()
  }

  const handleSettingPress = (action: (typeof settingsActions)[0]) => {
    if (action.route) {
      navigate(action.route)
      return
    }
    if (action.link) {
      window.open(action.link, '_blank')
    }
  }

  const { data: featureSettings } = useGetFeaturesSettings()

  const rewardsEnabled = featureSettings?.rewards?.enabled
  const rewardsLimit = featureSettings?.reward_limit_formatted
  const aiEnabled = config.VITE_OPENAI_KEY

  return (
    <BaseView flex={1} bgColor="background.screen" className="h-full">
      {/* Fixed Header Navigation */}
      <StickyHeaderNavigation
        title={t('screens:profile.profileSettings.title')}
        onBackPress={handleBackPress}
      />

      {/* Scrollable Content */}
      <BaseView
        flex={1}
        px={12}
        style={{
          overflowY: 'auto',
          paddingBottom: '140px', // Space for logout button
        }}
      >
        <BaseView gap={26}>
          {/* Profile Card */}
          <CardProfile />
          {/* <SettingItem
            icon={ReactIcon}
            circleSize={56}
            iconSize={17}
            iconBgColor="linear-gradient(105deg, #7866FF -0.69%, #4732FF 101.18%)"
            title="Kem Ai"
            description="Ask questions and get answers"
            onPress={() => {
              navigate('/ai-support')
            }}
          /> */}
          {rewardsEnabled && (
            <SettingItem
              icon={Plus}
              circleSize={56}
              iconSize={17}
              iconBgColor="linear-gradient(105deg, #7866FF -0.69%, #4732FF 101.18%)"
              title="Invite Friends"
              description={`Earn up to ${rewardsLimit}`}
              onPress={() => {
                navigate('/invite-friends')
              }}
            />
          )}

          {/* Customer Support */}
          <SettingItem
            icon={Support}
            circleSize={56}
            iconSize={17}
            iconBgColor="linear-gradient(105deg, #7866FF -0.69%, #4732FF 101.18%)"
            title={t('links:contactSupport')}
            description={t('translation:contactSupport')}
            onPress={() => {
              if (typeof window.Intercom === 'function') {
                window.Intercom('show')
              }
            }}
          />

          {/* Settings Title */}
          <BaseText fontSize={18} fontFamily="semibold" color="label.default">
            {t('screens:profile.profileSettings.subtitle')}
          </BaseText>
        </BaseView>

        {/* Settings List */}
        <BaseView gap={8} mt={16} mb={24}>
          {settingsActions.map((action) => (
            <SettingItem
              key={action.id}
              title={t(action.titleKey)}
              description={t(action.descriptionKey)}
              icon={action.icon}
              iconColor={action.iconColor}
              iconBgColor={action.iconBgColor}
              onPress={() => handleSettingPress(action)}
            />
          ))}
        </BaseView>
      </BaseView>

      {/* Fixed Logout Section */}
      <BaseView
        px={12}
        py={24}
        bgColor="background.screen"
        gap={16}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
        }}
      >
        <AppButton
          type="secondaryGray"
          label={t('form:actions.logout')}
          onClick={handleLogout}
          isLoading={isPending}
        />

        {/* Version info */}
        <BaseText
          fontSize={12}
          fontFamily="regular"
          color="label.warningDescription"
          textAlign="center"
        >
          V. {config.APP_VERSION} - WEB
        </BaseText>
      </BaseView>
    </BaseView>
  )
}
